import { Component, type ReactNode } from 'react';
import { IconAlertTriangle, IconRefresh, IconHome } from '@tabler/icons-react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorId?: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    return { hasError: true, error, errorId };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Error caught:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
    });

    this.props.onError?.(error, errorInfo);
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorId: undefined });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="page page-center">
          <div className="container container-tight py-4">
            <div className="empty">
              <div className="empty-header">
                <IconAlertTriangle size={48} className="text-danger" />
              </div>
              <p className="empty-title">Wystąpił nieoczekiwany błąd</p>
              <p className="empty-subtitle text-muted">
                Przepraszamy za niedogodności. Aplikacja napotkała problem, który uniemożliwia prawidłowe działanie.
              </p>

              <div className="empty-action">
                <div className="btn-group" role="group">
                  <button className="btn btn-primary" onClick={this.handleRetry}>
                    <IconRefresh size={16} className="me-1" />
                    Spróbuj ponownie
                  </button>
                  <button className="btn btn-outline-primary" onClick={this.handleRefresh}>
                    <IconRefresh size={16} className="me-1" />
                    Odśwież stronę
                  </button>
                  <button className="btn btn-outline-secondary" onClick={this.handleGoHome}>
                    <IconHome size={16} className="me-1" />
                    Strona główna
                  </button>
                </div>
              </div>

              {this.state.error && (
                <div className="mt-4">
                  <details className="text-start">
                    <summary className="text-muted">Szczegóły błędu (tryb rozwoju)</summary>
                    <div className="mt-2">
                      <div className="mb-2">
                        <strong>Error ID:</strong> <code>{this.state.errorId}</code>
                      </div>
                      <div className="mb-2">
                        <strong>Message:</strong> <code>{this.state.error.message}</code>
                      </div>
                      {this.state.error.stack && (
                        <div>
                          <strong>Stack trace:</strong>
                          <pre className="text-xs bg-light p-2 mt-1 rounded">{this.state.error.stack}</pre>
                        </div>
                      )}
                    </div>
                  </details>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
